import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export interface UserInfo {
  id: string;
  email: string;
  displayName: string;
  role: string;
}

interface AuthState {
  user: UserInfo | null;
  accessToken: string | null;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  checkEmail: (email: string) => Promise<{ exists: boolean; requiresSetup: boolean }>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  setupPassword: (email: string, password: string) => Promise<void>;
  firstSetup: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface LoginResponse {
  accessToken: string;
  user: UserInfo;
}

async function refreshTokens(): Promise<LoginResponse | null> {
  const res = await fetch('/api/auth/refresh', {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) return null;
  return res.json() as Promise<LoginResponse>;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    isLoading: true,
  });

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleRefresh = useCallback((accessToken: string) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]!)) as { exp: number };
      const delay = Math.max(0, payload.exp * 1000 - Date.now() - 60_000);
      refreshTimerRef.current = setTimeout(() => { void silentRefresh(); }, delay);
    } catch {
      // ignore malformed token
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const silentRefresh = useCallback(async () => {
    const data = await refreshTokens();
    if (data) {
      setState({ user: data.user, accessToken: data.accessToken, isLoading: false });
      scheduleRefresh(data.accessToken);
    } else {
      setState({ user: null, accessToken: null, isLoading: false });
    }
  }, [scheduleRefresh]);

  useEffect(() => {
    void silentRefresh();
    return () => { if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current); };
  }, [silentRefresh]);

  const checkEmail = useCallback(async (email: string) => {
    const res = await fetch('/api/auth/check-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    return res.json() as Promise<{ exists: boolean; requiresSetup: boolean }>;
  }, []);

  const loginWithEmail = useCallback(async (email: string, password: string) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(data.error ?? 'Erreur de connexion.');
    }
    const data = await res.json() as LoginResponse;
    setState({ user: data.user, accessToken: data.accessToken, isLoading: false });
    scheduleRefresh(data.accessToken);
  }, [scheduleRefresh]);

  const setupPassword = useCallback(async (email: string, password: string) => {
    const res = await fetch('/api/auth/setup-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(data.error ?? 'Erreur lors de la configuration du mot de passe.');
    }
    const data = await res.json() as LoginResponse;
    setState({ user: data.user, accessToken: data.accessToken, isLoading: false });
    scheduleRefresh(data.accessToken);
  }, [scheduleRefresh]);

  const firstSetup = useCallback(async (email: string, password: string, displayName: string) => {
    const res = await fetch('/api/auth/first-setup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, displayName }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(data.error ?? 'Erreur lors de la configuration.');
    }
    const data = await res.json() as LoginResponse;
    setState({ user: data.user, accessToken: data.accessToken, isLoading: false });
    scheduleRefresh(data.accessToken);
  }, [scheduleRefresh]);

  const logout = useCallback(async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    setState({ user: null, accessToken: null, isLoading: false });
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (state.accessToken) return state.accessToken;
    const data = await refreshTokens();
    if (data) {
      setState({ user: data.user, accessToken: data.accessToken, isLoading: false });
      scheduleRefresh(data.accessToken);
      return data.accessToken;
    }
    return null;
  }, [state.accessToken, scheduleRefresh]);

  return (
    <AuthContext.Provider value={{ ...state, checkEmail, loginWithEmail, setupPassword, firstSetup, logout, getAccessToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
